import {
	preferredFiatCurrencyAtom,
	type PreParsedSectionItemsSettingProps,
	type PreParsedSectionSettingProps,
	type SectionAction,
	useFiatCurrenciesQuery,
	useIntl,
	useLocaleLabel,
	useMeFavouritePartner
} from '@capsicum/common';
import { useMe } from '@capsicum/socios-core';
import { useAtom } from 'jotai';
import { useWithdrawalPassword } from 'libs/features/account/shared/src/hooks';
import { useCallback, useEffect, useMemo } from 'react';

import { isASectionAction } from '../util/isASectionAction';

const useSectionActionDefinitions = (): SectionAction => {
	const { me } = useMe();
	const { $t } = useIntl();
	const { favoritePartner } = useMeFavouritePartner();

	const preferredFiatCurrency = me.preferredFiatCurrencyCode;
	const { withdrawalPasswordExists } = useWithdrawalPassword();
	const [preferredFiatCurrencyAtomInfo, setPreferredFiatCurrencyAtom] = useAtom(preferredFiatCurrencyAtom);

	const { data } = useFiatCurrenciesQuery({
		options: { enabled: !!preferredFiatCurrency },
		queryFnParams: !preferredFiatCurrency ? undefined : { 'shortCodes[]': [preferredFiatCurrency] }
	});

	useEffect(() => {
		const currency = data && data[0];

		if (!currency) return;

		setPreferredFiatCurrencyAtom({
			id: currency.id,
			shortCode: currency.shortCode,
			symbol: currency.symbol
		});
	}, [data, setPreferredFiatCurrencyAtom]);

	const currency = useMemo(() => {
		if (!preferredFiatCurrencyAtomInfo) return preferredFiatCurrency;

		const { shortCode, symbol } = preferredFiatCurrencyAtomInfo;

		return `${shortCode} (${symbol})`;
	}, [preferredFiatCurrency, preferredFiatCurrencyAtomInfo]);

	return {
		applyRecoveryKit: $t('onboard.settings.applyRecoveryKit'),
		configureEmailNotifications: $t('onboard.settings.configure'),
		fetchCurrency: currency,
		fetchCurrentWithdrawalPassword: withdrawalPasswordExists
			? $t('onboard.settings.change')
			: $t('onboard.settings.add'),
		fetchFavoritePartner: favoritePartner?.name || $t('onboard.settings.add'),
		fetchLanguage: useLocaleLabel(),
		generateRecoveryKit: $t('onboard.settings.generate')
	};
};

export const useSetSectionActions = (sections: Array<PreParsedSectionSettingProps>) => {
	const sectionActionDefinitions = useSectionActionDefinitions();

	const setItemAction = useCallback(
		(item: PreParsedSectionItemsSettingProps) => {
			const { action } = item;

			return {
				...item,
				customAction: action
					? isASectionAction(action)
						? sectionActionDefinitions[action]
						: undefined
					: undefined
			};
		},
		[sectionActionDefinitions]
	);

	return useMemo(
		() =>
			sections.map(section => ({
				items: section.items.map(setItemAction),
				key: section.key,
				title: section.title
			})),
		[sections, setItemAction]
	);
};
